import React, { useEffect, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    Card, Row, Col, Input, Button, Space,
    Typography, Popconfirm, Tag, Empty,
    Badge, Select, Modal, Divider, Grid, message
} from "antd";
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
    UploadOutlined, DownloadOutlined, FileExcelOutlined,
    UserOutlined, SaveOutlined
} from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import { CSVLink } from 'react-csv';
import { getRequest, postRequest } from "../../../Utils/AppApi";
import {
    SAVE_OUTLET_ID,
    LIST_OUTLET_ID,
    DELETE_OUTLET_ID,
    BULK_OUTLET_UPLOAD,
    EXPORT_OUTLET_ID,
    ADMIN_GET_ALL_APPROVED_USERS
} from "../../../Utils/appConstants";

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

// Helpers to safely extract user data
const getUserName = (user) => {
    return user?.userName || user?.user?.userName || user?.username || user?.user?.username || "";
};
const getDisplayName = (user) => {
    return user?.user?.name || user?.name || "";
};

const OutletIdPage = ({ history }) => {
    const csvLink = useRef();
    const [form, setForm] = useState({ name: "", userName: "", outletId: "", status: "1" });
    const [data, setData] = useState([]);
    const [csvData, setCsvData] = useState([]);
    const [editing, setEditing] = useState(null);
    const [loading, setLoading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [removingId, setRemovingId] = useState(null);
    const [operator, setOperator] = useState("JIO");
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [recordsPerPage, setRecordsPerPage] = useState(10);

    // User modal state
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [savingField, setSavingField] = useState(null);

    // Editable values for modal
    const [editOutletId, setEditOutletId] = useState("");

    const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

    // Fetch approved users - FILTER to only those with a generic outletId
    const fetchApprovedUsers = useCallback(() => {
        setLoadingUsers(true);
        getRequest(`${ADMIN_GET_ALL_APPROVED_USERS}?roleId=64c75becf5b473e0af8ba9fb&exportsCsv=true`, history)
            .then(res => {
                if (res?.success && Array.isArray(res.data)) {
                    // ✅ Only keep users who have a non‑empty generic outletId
                    const filteredUsers = res.data.filter(user => user.outletId && user.outletId.trim() !== "");
                    setUsers(filteredUsers);
                    if (filteredUsers.length === 0) {
                        toast.info("No users with generic outlet ID found");
                    }
                } else {
                    setUsers([]);
                }
            })
            .catch(err => {
                console.error(err);
                toast.error("Failed to load users");
                setUsers([]);
            })
            .finally(() => setLoadingUsers(false));
    }, [history]);

    useEffect(() => { fetchApprovedUsers(); }, [fetchApprovedUsers]);

    // Fetch existing outlet ID record for a user + operator (used in save)
    const fetchExistingOutletId = async (userName, targetOperator) => {
        try {
            const res = await getRequest(`${LIST_OUTLET_ID}?operator=${targetOperator}`, history);
            if (res?.success && Array.isArray(res.data?.records)) {
                const found = res.data.records.find(record => record.userName === userName);
                return found || null;
            }
            return null;
        } catch (error) {
            console.error("Error checking existing outlet:", error);
            return null;
        }
    };

    // Handle user selection → open modal
    const handleUserSelect = (userId) => {
        setSelectedUserId(userId);
        if (!userId) {
            setSelectedUser(null);
            setModalVisible(false);
            return;
        }
        const user = users.find(u => u._id === userId);
        if (user) {
            setSelectedUser(user);
            setEditOutletId(user.outletId || "");
            setModalVisible(true);
        }
    };

    // Save function for modal
    const saveOutletField = async (fieldValue, targetOperator, fieldName) => {

        if (!selectedUser) return;

        if (!fieldValue) {
            toast.error("Outlet ID cannot be empty");
            return;
        }

        setSavingField(fieldName);

        try {

            const userName = getUserName(selectedUser);
            const name = getDisplayName(selectedUser);

            const existing = await fetchExistingOutletId(
                userName,
                targetOperator
            );

            // PREVENT SAME VALUE SAVE
            if (
                existing &&
                existing.outletId?.trim() === fieldValue?.trim()
            ) {
                toast.error(`Outlet ID "${fieldValue}" already exists`);
                setSavingField(null);
                return;
            }

            let payload;

            if (existing) {

                payload = {
                    id: existing._id,
                    name,
                    userName,
                    outletId: fieldValue,
                    status: "1",
                    operator: targetOperator
                };

            } else {

                payload = {
                    name,
                    userName,
                    outletId: fieldValue,
                    status: "1",
                    operator: targetOperator
                };
            }

            const res = await postRequest(
                SAVE_OUTLET_ID,
                payload,
                history
            );

            if (res?.success) {

                toast.success(`${fieldName} saved successfully`);

                setModalVisible(false);
                setSelectedUser(null);
                setSelectedUserId(null);

                await fetchApprovedUsers();
                await fetchData();

            } else {

                toast.error(res?.message || "Save failed");
            }

        } catch (error) {

            console.error(error);
            toast.error("Error saving outlet ID");

        } finally {

            setSavingField(null);
        }
    };

    // Fetch outlet IDs for current operator with pagination
    const fetchData = useCallback(async (page = currentPage, limit = recordsPerPage) => {
        setLoading(true);
        try {
            const res = await getRequest(
                `${LIST_OUTLET_ID}?operator=${operator}&page=${page}&limit=${limit}`,
                history
            );

            if (res?.success) {
                const records = Array.isArray(res.data?.records) ? res.data.records : [];
                setData(records);

                // Update pagination info
                if (res.data?.pagination) {
                    setTotalRecords(res.data.pagination.total || 0);
                    setCurrentPage(res.data.pagination.page || 1);
                    setRecordsPerPage(res.data.pagination.limit || 10);
                }
            } else {
                setData([]);
                setTotalRecords(0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            toast.error("Failed to load data");
            setData([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    }, [history, operator]);

    // Handle page change
    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchData(page, recordsPerPage);
    };

    // Handle rows per page change
    const handleRowsPerPageChange = (newLimit, page) => {
        setRecordsPerPage(newLimit);
        setCurrentPage(1);
        fetchData(1, newLimit);
    };

    // Fetch data when operator changes
    useEffect(() => {
        setCurrentPage(1);
        fetchData(1, recordsPerPage);
    }, [operator]);

    const handleSave = async () => {

        if (!form.userName || !form.outletId) {
            return toast.error("Username & Outlet ID required");
        }

        setActionLoading(true);

        try {

            // CHECK DUPLICATE ONLY WHILE ADDING
            if (!editing) {

                const duplicate = data.find(item =>
                    item.outletId?.trim() === form.outletId?.trim() &&
                    item.operator === operator
                );

                if (duplicate) {
                    toast.error(`Outlet ID "${form.outletId}" already exists`);
                    setActionLoading(false);
                    return;
                }
            }

            // UPDATE CALL ONLY WHEN EDITING
            const payload = editing
                ? {
                    ...form,
                    id: editing._id,
                    operator
                }
                : {
                    ...form,
                    operator
                };

            const res = await postRequest(
                SAVE_OUTLET_ID,
                payload,
                history
            );

            if (res?.success) {

                toast.success(res.message);

                setForm({
                    name: "",
                    userName: "",
                    outletId: "",
                    status: "1"
                });

                setEditing(null);

                // Stay on current page after save
                await fetchData(currentPage, recordsPerPage);
                await fetchApprovedUsers();

            } else {

                toast.error(res?.message || "Save failed");
            }

        } catch (error) {

            console.error(error);
            toast.error("Error saving");

        } finally {

            setActionLoading(false);
        }
    };

    const handleDelete = async (id) => {
        setRemovingId(id);
        try {
            const res = await postRequest(DELETE_OUTLET_ID, { id, operator }, history);
            if (res?.success) {
                toast.success("Deleted");
                // Refresh current page
                await fetchData(currentPage, recordsPerPage);
                await fetchApprovedUsers();
            } else toast.error(res?.message || "Delete failed");
        } catch { toast.error("Delete failed"); }
        finally { setRemovingId(null); }
    };

    // Helper function to convert params to query string
    const objectToQueryString = (obj) => {
        var str = [];
        for (var p in obj)
            if (obj.hasOwnProperty(p)) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
            }
        return str.join("&");
    }

    // WORKING EXPORT FUNCTION using CSVLink (like reference code)
    const getCSVDataList = () => {
        setExporting(true);

        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const exportUrl = `${baseUrl}${EXPORT_OUTLET_ID}?exportsCsv=true&operator=${operator}&t=${Date.now()}`;

        fetch(exportUrl, {
            method: 'GET',
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
                'Accept': 'text/csv, application/json, */*'
            }
        })
            .then(response => {
                if (response.status === 403) {
                    throw new Error('Authentication failed. Please login again.');
                }
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.text();
            })
            .then(csvText => {
                if (!csvText || csvText.trim() === '') {
                    toast.error("No data to export");
                    return;
                }

                // Check if response is actually JSON error
                if (csvText.trim().startsWith('{')) {
                    try {
                        const errorData = JSON.parse(csvText);
                        toast.error(errorData.message || "Export failed");
                        return;
                    } catch (e) {
                        // Not JSON, continue with CSV
                    }
                }

                // Create blob and download
                const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `outlet_ids_${operator}_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

                // Count records
                const lines = csvText.trim().split('\n');
                const recordCount = Math.max(0, lines.length - 1);
                toast.success(`Export successful: ${recordCount} records exported`);
            })
            .catch(error => {
                console.error("Export error:", error);
                toast.error(error.message || "Export failed. Please try again.");
            })
            .finally(() => {
                setExporting(false);
            });
    };
    const handleImportUpload = async (file) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("operator", operator);
        setImporting(true);
        try {
            const res = await postRequest(BULK_OUTLET_UPLOAD, formData, history, true);
            if (res?.success) {
                toast.success(res.message);
                if (res.errors) {
                    Modal.error({
                        title: 'Import Errors',
                        content: <div style={{ maxHeight: 300, overflow: 'auto' }}>{res.errors.map((err, i) => <div key={i}>• {err}</div>)}</div>,
                        width: 600
                    });
                }
                await fetchData(1, recordsPerPage);
                await fetchApprovedUsers();
                setImportModalVisible(false);
            } else toast.error(res?.message || "Import failed");
        } catch { toast.error("Import failed"); }
        finally { setImporting(false); }
        return false;
    };

    const downloadSample = () => {
        const baseUrl = process.env.REACT_APP_API_URL || '';
        const sampleUrl = `${baseUrl}/admin/sampleOutletCsv`;
        window.open(sampleUrl, '_blank');
    };

    // Table columns
    const columns = [
        { name: "Name", selector: row => row.name || "-", sortable: true },
        { name: "Username", selector: row => row.userName || "-", sortable: true },
        { name: "Outlet ID", selector: row => row.outletId || "-", sortable: true },
        {
            name: "Used By",
            selector: row => row.usedCount || "-",
            sortable: true
        },
        { name: "Status", cell: row => <Tag color={row.status === "1" ? "green" : "red"}>{row.status === "1" ? "Active" : "Inactive"}</Tag>, sortable: true },
        { name: "Created", cell: row => row.createdAt ? moment(row.createdAt).format("DD/MM/YYYY") : "-", sortable: true },
        {
            name: "Actions", cell: row => (
                <Space>
                    <Button icon={<EditOutlined />} size="small" onClick={() => { setEditing(row); setForm({ name: row.name || "", userName: row.userName || "", outletId: row.outletId || "", status: row.status || "1" }); }} />
                    <Popconfirm title="Delete?" onConfirm={() => handleDelete(row._id)}>
                        <Button danger size="small" loading={removingId === row._id} icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    // Pagination options for DataTable
    const paginationOptions = {
        rowsPerPageText: 'Records per page:',
        rangeSeparatorText: 'of',
        selectAllRowsItem: true,
        selectAllRowsItemText: 'All'
    };

    // Operator options
    const operatorOptions = [
        { value: "JIO", label: "JIO" },
        { value: "NSDL", label: "NSDL" },
        { value: "AIRTEL", label: "AIRTEL" },
        { value: "UPIQR", label: "UPI QR" }
    ];

    return (
        <div style={{ background: "#F3F4F6", minHeight: "100vh", padding: 32 }}>
            <div style={{ maxWidth: 1440, margin: "0 auto" }}>
                {/* Import Modal */}
                <Modal
                    title="Import Outlet IDs"
                    open={importModalVisible}
                    onCancel={() => setImportModalVisible(false)}
                    footer={null}
                    width={500}
                >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Text>Download sample CSV file to understand the format:</Text>
                        <Button icon={<DownloadOutlined />} onClick={downloadSample}>Download Sample</Button>
                        <Divider />
                        <Text>Upload your CSV file:</Text>
                        <input
                            type="file"
                            accept=".csv"
                            onChange={(e) => {
                                if (e.target.files[0]) handleImportUpload(e.target.files[0]);
                            }}
                            disabled={importing}
                        />
                        {importing && <Text type="secondary">Importing...</Text>}
                    </Space>
                </Modal>

                {/* CSV Export Link - Hidden */}
                <CSVLink
                    data={csvData}
                    filename={`outlet_ids_${operator}_${moment().format('YYYY-MM-DD')}`}
                    className='hidden'
                    ref={csvLink}
                    target='_blank'
                />

                {/* Operator Selection Card */}
                <Card style={{ marginBottom: 24, borderRadius: 20, boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)", border: "none" }} bodyStyle={{ padding: "16px 24px" }}>
                    <Space size="large">
                        <Text strong style={{ fontSize: 16, color: "#1F2937" }}>OPERATOR</Text>
                        <Select value={operator} onChange={setOperator} style={{ width: 140 }} size="large" className="modern-select">
                            {operatorOptions.map(opt => (
                                <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                            ))}
                        </Select>
                    </Space>
                </Card>

                {/* User Selector Card */}
                <Card style={{ marginBottom: 32, borderRadius: 20, boxShadow: "0 1px 3px 0 rgba(0,0,0,0.05)", border: "none" }}
                    title={<Space><UserOutlined style={{ color: "#3B82F6" }} /><Text strong style={{ fontSize: 16 }}>Select Approved User (with generic Outlet ID)</Text></Space>}
                    headStyle={{ borderBottom: "1px solid #E5E7EB", padding: "16px 24px" }}
                    bodyStyle={{ padding: "24px" }}
                >
                    <Select
                        showSearch
                        placeholder="Search by username..."
                        style={{ width: '100%' }}
                        size="large"
                        loading={loadingUsers}
                        value={selectedUserId}
                        onChange={handleUserSelect}
                        filterOption={(input, option) => option?.props?.children?.toString()?.toLowerCase().includes(input.toLowerCase())}
                        notFoundContent={loadingUsers ? "Loading..." : "No users found"}
                        allowClear
                        className="modern-select"
                    >
                        {users.map(user => {
                            const username = getUserName(user);
                            return <Option key={user._id} value={user._id}>{username || "Unnamed User"}</Option>;
                        })}
                    </Select>
                </Card>

                {/* User Details Modal */}
                <Modal
                    title={<Space><UserOutlined style={{ color: "#3B82F6", fontSize: 20 }} /><Text strong style={{ fontSize: 18 }}>Manage Outlet ID</Text><Text type="secondary">— {getUserName(selectedUser) || "User"}</Text></Space>}
                    open={modalVisible}
                    onCancel={() => { setModalVisible(false); setSelectedUser(null); setSelectedUserId(null); }}
                    footer={null}
                    width={720}
                    destroyOnClose
                    bodyStyle={{ padding: "24px" }}
                    maskClosable={false}
                >
                    {selectedUser && (
                        <>
                            <Card style={{ background: "#F9FAFB", borderRadius: 16, border: "none", marginBottom: 24 }}>
                                <Row gutter={[16, 16]}>
                                    <Col span={24}><div><Text type="secondary">Name</Text><br /><Text strong style={{ fontSize: 16 }}>{getDisplayName(selectedUser) || '—'}</Text></div></Col>
                                    <Col span={24}><div><Text type="secondary">Username</Text><br /><Text strong style={{ fontSize: 16 }}>{getUserName(selectedUser) || '—'}</Text></div></Col>
                                </Row>
                            </Card>
                            <Divider orientation="left" style={{ margin: "0 0 24px 0", fontSize: 14, color: "#6B7280" }}>Outlet ID</Divider>
                            <Row gutter={[16, 20]}>
                                <Col xs={24} sm={18}>
                                    <Input
                                        addonBefore={`Outlet ID (${operator})`}
                                        value={editOutletId}
                                        onChange={(e) => setEditOutletId(e.target.value)}
                                        placeholder={`Enter ${operator} outlet ID`}
                                        size="large"
                                        className="modern-input"
                                    />
                                </Col>
                                <Col xs={24} sm={6}>
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        onClick={() => saveOutletField(editOutletId, operator, "outletId")}
                                        loading={savingField === "outletId"}
                                        block
                                        size="large"
                                        style={{ height: "100%", borderRadius: 12 }}
                                    >
                                        Save
                                    </Button>
                                </Col>
                            </Row>
                        </>
                    )}
                </Modal>

                {/* Left Form and DataTable */}
                <Row gutter={[32, 32]}>
                    <Col xs={24} lg={8}>
                        <Card style={{ borderRadius: 20, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "none", height: "100%" }}
                            title={
                                <Space style={{ justifyContent: "space-between", width: "100%" }}>
                                    <Text strong style={{ fontSize: 18 }}>{editing ? "Update Outlet ID" : "Add New Outlet ID"}</Text>
                                    <Button
                                        icon={<UploadOutlined />}
                                        onClick={() => setImportModalVisible(true)}
                                        size="small"
                                    >
                                        Import
                                    </Button>
                                </Space>
                            }
                            headStyle={{ borderBottom: "1px solid #E5E7EB", padding: "20px 24px" }}
                            bodyStyle={{ padding: "24px" }}
                        >
                            <Space direction="vertical" style={{ width: "100%" }} size="large">
                                <Input
                                    placeholder="Name"
                                    value={form.name}
                                    onChange={e => handleChange("name", e.target.value)}
                                    size="large"
                                    className="modern-input"
                                />
                                <Input
                                    placeholder="Username *"
                                    value={form.userName}
                                    onChange={e => handleChange("userName", e.target.value)}
                                    size="large"
                                    className="modern-input"
                                />
                                <Input
                                    placeholder="Outlet ID *"
                                    value={form.outletId}
                                    onChange={e => handleChange("outletId", e.target.value)}
                                    size="large"
                                    className="modern-input"
                                />
                                <Select
                                    value={form.status}
                                    onChange={val => handleChange("status", val)}
                                    size="large"
                                    className="modern-select"
                                >
                                    <Option value="1">Active</Option>
                                    <Option value="0">Inactive</Option>
                                </Select>
                                <Button
                                    type="primary"
                                    block
                                    icon={editing ? <EditOutlined /> : <PlusOutlined />}
                                    loading={actionLoading}
                                    onClick={handleSave}
                                    size="large"
                                    style={{ height: 48, borderRadius: 12, background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)", border: "none", fontWeight: 500, boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)" }}
                                >
                                    {editing ? "Update Outlet" : "Add Outlet"}
                                </Button>
                            </Space>
                        </Card>
                    </Col>
                    <Col xs={24} lg={16}>
                        <Card style={{ borderRadius: 20, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)", border: "none" }} bodyStyle={{ padding: "24px" }}>
                            <Space style={{ justifyContent: "space-between", width: "100%", marginBottom: 24 }} wrap>
                                <Title level={4} style={{ margin: 0, fontWeight: 600, color: "#1F2937" }}>Outlet IDs ({operator})</Title>
                                <Space>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        onClick={getCSVDataList}
                                        loading={exporting}
                                        style={{ borderRadius: 10 }}
                                    >
                                        Export
                                    </Button>
                                    <Badge
                                        count={totalRecords || 0}
                                        showZero
                                        style={{ backgroundColor: '#3B82F6' }}
                                        overflowCount={9999}
                                    />
                                </Space>
                            </Space>
                            <DataTable
                                columns={columns}
                                data={data}
                                progressPending={loading}
                                pagination
                                paginationServer
                                paginationTotalRows={totalRecords}
                                onChangePage={handlePageChange}
                                onChangeRowsPerPage={handleRowsPerPageChange}
                                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                                paginationComponentOptions={paginationOptions}
                                highlightOnHover
                                noDataComponent={<Empty description={`No ${operator} outlet IDs found`} image={Empty.PRESENTED_IMAGE_SIMPLE} />}
                                customStyles={{
                                    headRow: { style: { backgroundColor: '#F9FAFB', fontWeight: 600, fontSize: '14px', borderBottom: '1px solid #E5E7EB', borderRadius: '12px 12px 0 0' } },
                                    rows: { style: { fontSize: '14px', cursor: 'pointer', '&:hover': { backgroundColor: '#F3F4F6' } } },
                                    pagination: { style: { borderTop: '1px solid #E5E7EB', paddingTop: '16px', marginTop: '16px' } }
                                }}
                            />
                        </Card>
                    </Col>
                </Row>
            </div>
            <style jsx>{`
                :global(.modern-input .ant-input) { border-radius: 12px; border: 1px solid #E5E7EB; transition: all 0.2s; }
                :global(.modern-input .ant-input:hover) { border-color: #3B82F6; }
                :global(.modern-input .ant-input:focus) { border-color: #3B82F6; box-shadow: 0 0 0 2px rgba(59,130,246,0.1); }
                :global(.modern-select .ant-select-selector) { border-radius: 12px !important; border: 1px solid #E5E7EB !important; transition: all 0.2s; }
                :global(.modern-select .ant-select-selector:hover) { border-color: #3B82F6 !important; }
                :global(.modern-select.ant-select-focused .ant-select-selector) { border-color: #3B82F6 !important; box-shadow: 0 0 0 2px rgba(59,130,246,0.1) !important; }
                :global(.ant-modal-content) { border-radius: 24px; overflow: hidden; }
                :global(.ant-modal-header) { border-bottom: 1px solid #E5E7EB; padding: 20px 24px; }
                :global(.ant-btn-primary) { background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); border: none; }
                :global(.ant-btn-primary:hover) { background: linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%); }
                :global(.ant-tag) { borderRadius: 20px; padding: 2px 12px; font-weight: 500; }
                :global(.hidden) { display: none; }
            `}</style>
        </div>
    );
};

export default OutletIdPage;